import Vue from 'vue'
import VueGtm from 'vue-gtm'
import store from '@/store'
import { objectHasKeys } from '@/utils/comparators'
import { getMTUCategoryStringForMParticle, getMTUSubCategoryStringForMParticle, getCallingSubCategoryStringForMParticle, getCallingCategoryStringForMParticle } from '@/utils/checkout'
import {
  TIKTOK_CLICK_ID,
  TIKTOK_COOKIE_ID
} from '@/utils/constants'
/**
* Initializes GTM for tracking
*/
const initializeGtm = () => {
  Vue.use(VueGtm, {
    enabled: true
    // debug: process.env.NODE_ENV !== 'production'
  })
}

/**
 * Adding the `tech_platform` field to mParticle events allows us to distinguish
 * the origin of events.
 */
const getTechPlatform = () => {
  return { tech_platform: 'my-rebtel' }
}

const trackEvent = (eventName, eventType, extraAttributes) => {
  // EventType
  // Unknown: 0, Location: 2, Navigation: 1, Other: 8, Search: 3, Social: 7, Transaction: 4, UserContent: 5, UserPreference: 6
  window.mParticle.ready(async () => {
    let userEmail = store.getters['user/getUserData'].email
    let userFirstName = store.getters['user/getUserData'].firstName
    let userLastName = store.getters['user/getUserData'].lastName
    let userPrimaryNumber = store.state.user.primaryNumber
    const sessionId = await getGA4SessionId()

    let eventAttributes = {
      session_id: sessionId || null,
      ...(process.env.ENV !== 'production' && { debug_mode: true }),
      ...getTechPlatform()
    }
    if (extraAttributes !== null && typeof extraAttributes === 'object') {
      Object.assign(eventAttributes, extraAttributes)
    }

    let customFlags = {
      'GoogleAds.ECData': {
        email: userEmail,
        phone_number: userPrimaryNumber,
        first_name: userFirstName,
        last_name: userLastName
      }
    }
    const tiktokClickId = window.$cookies.get(TIKTOK_CLICK_ID)
    if (tiktokClickId) {
      customFlags = {
        ...customFlags,
        'TikTok.URL': window.location.origin + window.location.pathname,
        'TikTok.Referrer': document.referrer ? document.referrer : '',
        'TikTok.Callback': tiktokClickId
      }
    }
    window.mParticle.logEvent(
      eventName,
      eventType,
      eventAttributes,
      customFlags
    )
    window.mParticle.upload()
  })
}

const trackPageView = (fragment, extraAttributes) => {
  window.mParticle.ready(async () => {
    try {
      let userLocale = store.getters['user/locale']
      let userCurency = store.getters['user/currency']
      let userEmail = store.getters['user/getUserData'].email
      let userFirstName = store.getters['user/getUserData'].firstName
      let userLastName = store.getters['user/getUserData'].lastName
      let userPrimaryNumber = store.state.user.primaryNumber
      let toCountry = store.getters['user/tracking/getTrackingInfo'].countryCode
      let carrier = store.getters['user/tracking/getTrackingInfo'].carrier
      let pageTitle = document.title
      const sessionId = await getGA4SessionId()

      let eventAttributes = {
        'title': pageTitle,
        'virtualPageURL': fragment,
        'virtual_page_url': window.location.origin + window.location.pathname,
        'full_page_url': window.location.href,
        'fullURL': window.location.href,
        'locale': userLocale,
        'currency': userCurency,
        'toCountry': toCountry || null,
        'carrier': carrier || null,
        'geolocation': null, // this will stay null until we implement a geolocation library in this framework
        'session_id': sessionId || null,
        ...(process.env.ENV !== 'production' && { debug_mode: true }),
        ...getTechPlatform()
      }
      let locationGA4 = window.location.origin + window.location.pathname
      if (extraAttributes !== null && typeof extraAttributes === 'object') {
        Object.assign(eventAttributes, extraAttributes)
        locationGA4 = extraAttributes.virtual_page_url
      }
      let customFlags = { 'GA4.Location': locationGA4 + window.location.search,
        'GA4.Title': pageTitle,
        'GoogleAds.ECData': {
          email: userEmail,
          phone_number: userPrimaryNumber,
          first_name: userFirstName,
          last_name: userLastName
        }
      }
      const tiktokClickId = window.$cookies.get(TIKTOK_CLICK_ID)
      if (tiktokClickId) {
        customFlags = {
          ...customFlags,
          'TikTok.URL': window.location.origin + window.location.pathname,
          'TikTok.Referrer': document.referrer ? document.referrer : '',
          'TikTok.Callback': tiktokClickId
        }
      }
      window.mParticle.logPageView(
        'Web Page',
        eventAttributes,
        customFlags
      )
      window.mParticle.upload()
    } catch (error) {
      console.log('some error', error)
    }
  })
}

const mParticleSetUserAttributes = (att) => {
  window.mParticle.ready(() => {
    try {
      const currentUser = window.mParticle.Identity.getCurrentUser()
      currentUser.setUserAttributes(att)
    } catch (error) {
      console.log('error')
    }
  })
}

const mParticleSetIntegrationAttributes = () => {
  window.mParticle.ready(() => {
    try {
      const integrationAttributes = window.mParticle.getIntegrationAttributes(160)
      if (objectHasKeys(integrationAttributes)) {
        mParticleSetUserAttributes(integrationAttributes)
      }
    } catch (error) {
      console.log('error')
    }
  })
}

const getGA4SessionId = () => {
  return new Promise((resolve, reject) => {
    try {
      const measurementId = process.env.GTM_ID
      window.gtag('get', measurementId, 'session_id', resolve)
    } catch (error) {
      reject(error)
    }
  })
}

const getGA4ClientId = () => {
  return new Promise((resolve, reject) => {
    try {
      const measurementId = process.env.GTM_ID
      window.gtag('get', measurementId, 'client_id', resolve)
    } catch (error) {
      reject(error)
    }
  })
}

const setUtmParams = async () => {
  const utmParams = ['utm_id', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_source_platform', 'utm_creative_format', 'utm_marketing_tactic', 'gclid', 'msclkid', 'awaid', 'awgid', 'awbid', 'awpid', 'awcr', 'awc']
  const searchParamsObject = {}
  const obj = {}
  const searchParams = new URLSearchParams(window.location.search)
  searchParams.forEach(function (value, key) {
    if (utmParams.includes(key) && value) {
      searchParamsObject[key] = value
    }
  })
  utmParams.forEach(function (key) {
    if (searchParamsObject[key]) {
      obj[key] = searchParamsObject[key]
    } else {
      obj[key] = null
    }
  })
  if (objectHasKeys(searchParamsObject)) {
    mParticleSetUserAttributes(obj)
  }
}

const mParticleCaptureError = (errorCode, errorBody, errorReason, errorRequest) => {
  window.mParticle.ready(() => {
    try {
      let customAttributes = {
        'error_reason': errorReason || null,
        'error_code': errorCode || null,
        'error_id': errorRequest || null,
        'error_message_user': errorBody || null
      }
      window.mParticle.logEvent(
        'error_mparticle_idsync',
        window.mParticle.EventType.Other, customAttributes
      )
    } catch (error) {
      console.log('error')
    }
  })
}

export const setTikTokParams = () => {
  const searchParams = new URLSearchParams(window.location.search)
  const tiktokClickIdFromUrl = searchParams.get(TIKTOK_CLICK_ID)

  if (tiktokClickIdFromUrl) {
    window.$cookies.set(TIKTOK_CLICK_ID, JSON.stringify(tiktokClickIdFromUrl), {
      expires: 30,
      domain:
        process.env.NODE_ENV !== 'production'
          ? 'localhost'
          : process.env.COOKIE_DOMAIN
    })
  }

  const tiktokClickIdFromCookie =
    !tiktokClickIdFromUrl && window.$cookies.get(TIKTOK_CLICK_ID)
      ? window.$cookies.get(TIKTOK_CLICK_ID)
      : null

  if (tiktokClickIdFromCookie || tiktokClickIdFromUrl) {
    mParticleSetUserAttributes({
      [TIKTOK_CLICK_ID]: tiktokClickIdFromUrl || tiktokClickIdFromCookie
    })
  }

  const tiktokCookieId = window.$cookies.get(TIKTOK_COOKIE_ID)

  if (tiktokCookieId) {
    mParticleSetUserAttributes({
      [TIKTOK_COOKIE_ID]: tiktokCookieId
    })
  }
}

const mParticleModify = (updateEmail) => {
  let identityRequest = {
    userIdentities: { email: updateEmail }
  }
  let identityCallback = (result) => {
    let codes = window.mParticle.Identity.HTTPCodes
    switch (result.httpCode) {
      case codes.noHttpCoverage:
        mParticleCaptureError(codes.noHttpCoverage, result.body, 'noHttpCoverage', 'Modify')
        // retry the IDSync request
        break
      case codes.activeIdentityRequest:
      case 429:
        mParticleCaptureError(codes.activeIdentityRequest, result.body, 'activeIdentityRequest', 'Modify')
        // inspect your implementation if this occurs frequency
        // otherwise retry the IDSync request
        break
      case codes.validationIssue:
      case 400:
        mParticleCaptureError(codes.validationIssue, result.body, 'validationIssue', 'Modify')
        console.log(result.body)
        // inspect result.body to determine why the request failed
        // this typically means an implementation issue
        break
      default:
        console.log('default case')
    }
  }
  window.mParticle.ready(() => {
    window.mParticle.Identity.modify(identityRequest, identityCallback)
    window.mParticle.upload()
  })
}

const mParticleLogout = () => {
  try {
    let identityCallback = (result) => {
      if (result.getUser()) {
      }
      let codes = window.mParticle.Identity.HTTPCodes
      switch (result.httpCode) {
        case codes.noHttpCoverage:
          mParticleCaptureError(codes.noHttpCoverage, result.body, 'noHttpCoverage', 'Logout')
          // retry the IDSync request
          break
        case codes.activeIdentityRequest:
        case 429:
          mParticleCaptureError(codes.activeIdentityRequest, result.body, 'activeIdentityRequest', 'Logout')
          // inspect your implementation if this occurs frequency
          // otherwise retry the IDSync request
          break
        case codes.validationIssue:
        case 400:
          mParticleCaptureError(codes.validationIssue, result.body, 'validationIssue', 'Logout')
          console.log(result.body)
          // inspect result.body to determine why the request failed
          // this typically means an implementation issue
          break
        default:
          console.log('default case')
      }
    }
    window.mParticle.ready(() => {
      window.mParticle.Identity.logout({}, identityCallback)
      window.mParticle.upload()
    })
  } catch (error) {
    console.log('logout error')
  }
}

const mParticleLogin = (userId, _callback) => {
  let email = store.getters['user/getUserData'].email
  let userPrimaryNumber = store.state.user.primaryNumber

  if (!window.mParticle) {
    return false
  }
  if (typeof window.mParticle.ready !== 'function') {
    console.error('window.mParticle.ready is not a function', window.mParticle)
    return false
  }
  window.mParticle.ready(() => {
    try {
      if (window.mParticle.Identity.getCurrentUser() === null || userId !== window.mParticle.Identity.getCurrentUser().getUserIdentities().userIdentities.customerid) {
        let identityRequest = {
          userIdentities: {
            customerid: userId,
            mobile_number: userPrimaryNumber || null,
            email: email || null
          }
        }
        let identityCallback = (result) => {
          if (result.getUser()) {
            let userHasMparticleEmail = window.mParticle.Identity.getCurrentUser().getUserIdentities().userIdentities.email
            if (email && userHasMparticleEmail !== email) {
              mParticleModify(email)
            }
            let prevUserId = result.getPreviousUser().getUserIdentities().userIdentities.customerid
            let currentUserId = result.getUser().getUserIdentities().userIdentities.customerid
            if (!prevUserId && currentUserId) {
              result.getUser().setUserAttributes(result.getPreviousUser().getAllUserAttributes())
              let aliasRequest = window.mParticle.Identity.createAliasRequest(result.getPreviousUser(), result.getUser())
              window.mParticle.Identity.aliasUsers(aliasRequest)
              window.mParticle.upload()
            }
          }

          let codes = window.mParticle.Identity.HTTPCodes
          switch (result.httpCode) {
            case codes.noHttpCoverage:
              mParticleCaptureError(codes.noHttpCoverage, result.body, 'noHttpCoverage', 'RendrLogin')
              // retry the IDSync request
              break
            case codes.activeIdentityRequest:
            case 429:
              mParticleCaptureError(codes.activeIdentityRequest, result.body, 'activeIdentityRequest', 'RendrLogin')
              // inspect your implementation if this occurs frequency
              // otherwise retry the IDSync request
              break
            case codes.validationIssue:
            case 400:
              mParticleCaptureError(codes.validationIssue, result.body, 'validationIssue', 'RendrLogin')
              console.log(result.body)
              // inspect result.body to determine why the request failed
              // this typically means an implementation issue
              break
            default:
              console.log('default case')
          }

          if (_callback) _callback()
        }
        window.mParticle.ready(() => {
          window.mParticle.Identity.login(identityRequest, identityCallback)
          window.mParticle.upload()
        })
      } else {
        if (_callback) _callback()
      }
    } catch (error) {
    }
  })
}
const trackCheckoutTypeECommerceEvent = async () => {
  const user = store.state.user
  const order = store.state.checkoutV2.order
  const mParticleProduct = store.state.checkoutV2.mParticleProducts
  if (window.mParticle && window.mParticle.eCommerce && window.mParticle.eCommerce.setCurrencyCode && window.mParticle.eCommerce.logProductAction) {
    let transactionAttributes = null
    let customAttributes
    const sessionId = await getGA4SessionId()
    if (order.legacy) {
      window.mParticle.eCommerce.setCurrencyCode(order.totalAmount.currencyId)
      customAttributes = {
        service_category: 'Calling',
        product_category: getCallingCategoryStringForMParticle(order.items[0]) || '',
        product_sub_category: getCallingSubCategoryStringForMParticle(order.items[0]) || '',
        origination_country: user.geolocation || '',
        origination_currency: user.currency || '',
        session_id: sessionId || null,
        ...getTechPlatform()
      }
    } else { // Checkout type eCommere event fro MTU.
      window.mParticle.eCommerce.setCurrencyCode(order.summary.netAmount.currency)
      if (order.items && order.items[0] && order.items[0].product) {
        customAttributes = {
          service_category: order.items[0].product.serviceCategory || '',
          product_category: getMTUCategoryStringForMParticle(order.items[0].product) || '',
          product_sub_category: getMTUSubCategoryStringForMParticle(order.items[0].product) || '',
          destination_country: order.items[0].product.countryId || '',
          destination_currency: order.items[0].product.value.currency || '',
          origination_country: user.geolocation || '',
          origination_currency: user.currency || '',
          session_id: sessionId || null,
          ...getTechPlatform()
        }
      }
    }

    var customFlags = null // if not passing any custom flags, pass null
    window.mParticle.eCommerce.logProductAction(
      window.mParticle.ProductActionType.Checkout,
      mParticleProduct,
      customAttributes,
      customFlags,
      transactionAttributes
    )
  }
}
export {
  initializeGtm,
  trackPageView,
  trackEvent,
  mParticleLogout,
  mParticleModify,
  mParticleLogin,
  mParticleCaptureError,
  setUtmParams,
  mParticleSetIntegrationAttributes,
  trackCheckoutTypeECommerceEvent,
  getGA4ClientId,
  getGA4SessionId,
  getTechPlatform
}



// WEBPACK FOOTER //
// ./src/utils/tracking.js